import type { FC, ReactNode } from 'react'
import type { Project } from '@/constants/content'

import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { Container } from '@/components/generals/container'
import { RevealText } from '@/components/generals/reveal-text'
import { HalftoneImage } from '@/components/dots/halftone-image'
import { SilkDrift } from '@/components/dots/silk-drift'
import { SectionLabel } from '@/components/landing/section-label'
import { PROJECTS } from '@/constants/content'

type CardLayout = {
    column: string
    media: string
    sizes: string
}

// An asymmetric editorial grid: wide, tall, tall, wide.
const LAYOUTS: Array<CardLayout> = [
    { column: 'lg:col-span-7', media: 'aspect-[4/3]', sizes: '(min-width: 1024px) 58vw, 100vw' },
    { column: 'lg:col-span-5 lg:mt-48', media: 'aspect-[4/3] lg:aspect-[4/5]', sizes: '(min-width: 1024px) 42vw, 100vw' },
    { column: 'lg:col-span-5', media: 'aspect-[4/3] lg:aspect-[4/5]', sizes: '(min-width: 1024px) 42vw, 100vw' },
    { column: 'lg:col-span-7 lg:mt-48', media: 'aspect-[4/3]', sizes: '(min-width: 1024px) 58vw, 100vw' }
]

interface ProjectCardProps {
    project: Project
    layout: CardLayout
}

const ProjectCard: FC<ProjectCardProps> = ({ project, layout }): ReactNode => {
    return (
        <article className={cn('flex flex-col', layout.column)}>
            <Reveal>
                <div className={cn('relative overflow-hidden border border-line bg-white', layout.media)}>
                    <HalftoneImage
                        src={project.image}
                        alt={project.imageAlt}
                        focusX={project.focusX}
                        focusY={project.focusY}
                        sizes={layout.sizes}
                        className='absolute inset-0'
                    />

                    <span className='pointer-events-none absolute top-4 left-4 z-10 bg-white px-2 py-1 font-mono text-[11px] uppercase tracking-[0.14em] text-ink'>
                        {project.year}
                    </span>
                </div>

                <div className='mt-6 flex items-start justify-between gap-6'>
                    <div className='min-w-0'>
                        <p className='font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground'>
                            {project.client}
                            <span aria-hidden='true' className='px-2 text-ink/25'>/</span>
                            {project.sector}
                        </p>

                        <h3 className='mt-3 max-w-xl text-2xl font-medium leading-[1.15] tracking-[-0.03em] text-ink md:text-[28px]'>
                            {project.title}
                        </h3>
                    </div>

                    <p className='shrink-0 text-right'>
                        <span className='block text-3xl font-medium tracking-[-0.04em] text-azure-600 md:text-4xl'>
                            {project.metric}
                        </span>
                        <span className='mt-1 block font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground'>
                            {project.metricLabel}
                        </span>
                    </p>
                </div>

                <ul className='mt-5 flex flex-wrap gap-2'>
                    {project.tags.map(tag => (
                        <li
                            key={tag}
                            className='rounded-full border border-line px-3 py-1 font-mono text-[11px] uppercase tracking-[0.08em] text-ink/70'
                        >
                            {tag}
                        </li>
                    ))}
                </ul>
            </Reveal>
        </article>
    )
}

export const WorkSection: FC = (): ReactNode => {
    return (
        <section id='work' className='relative isolate border-t border-line bg-white'>
            <SilkDrift />

            <Container className='py-24 md:py-36'>
                <div className='grid gap-10 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-8'>
                        <SectionLabel index='03'>Selected work</SectionLabel>

                        <RevealText
                            lines={["Recent work,", "measured in outcomes."]}
                            className='mt-8 text-[clamp(2.5rem,5.4vw,5rem)] font-medium leading-[0.95] tracking-[-0.045em] text-ink'
                        />
                    </div>

                    <Reveal className='lg:col-span-4'>
                        <p className='text-lg leading-relaxed text-ink/70'>
                            A few of the products we designed, built and still run with our clients. Hover a project to see the original photo behind the dots.
                        </p>

                        <a
                            href='#contact'
                            className='group mt-6 inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.12em] text-ink'
                        >
                            Request full case studies
                            <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-1' />
                        </a>
                    </Reveal>
                </div>

                <div className='mt-16 grid gap-x-8 gap-y-16 md:mt-24 lg:grid-cols-12 lg:gap-y-24'>
                    {PROJECTS.map((project, index) => (
                        <ProjectCard
                            key={project.client}
                            project={project}
                            layout={LAYOUTS[index % LAYOUTS.length]}
                        />
                    ))}
                </div>
            </Container>
        </section>
    )
}
